import UpcommingScreen from "../components/status/UpcommingScreen.jsx";
import PostponedScreen from "../components/status/PostponedScreen.jsx";
import TBAScreen from "../components/status/TBAScreen.jsx";
import SessionEndedScreen from "../components/status/SessionEndedScreen.jsx";
import OnHoldScreen from "../components/status/OnHoldScreen.jsx";
import CanceledScreen from "../components/status/CanceledScreen.jsx";
import RescheduledScreen from "../components/status/RescheduledScreen.jsx";
import UnknownStatusScreen from "../components/status/UnknownStatusScreen.jsx";

// Used by MainPage (the single admin page, /admin/sessiondetails/:id/dashboard):
// shows loading / 404 / non-live status screens, and only renders children() once the
// session is loaded AND live (status 2).

const SESSION_STATUS = {
  UPCOMING: 1,
  LIVE: 2,
  POSTPONED: 5,
  TBA: 4,
  ENDED: 3,
  ON_HOLD: 8,
  CANCELED: 6,
  RESCHEDULED: 7,
};

const UnknownScreen = () => <UnknownStatusScreen />;

const STATUS_PAGES = {
  [SESSION_STATUS.UPCOMING]: ({ session }) => <UpcommingScreen title={session?.data?.detail?.title} />,
  [SESSION_STATUS.POSTPONED]: ({ session }) => <PostponedScreen sessionTitle={session?.data?.detail?.title} />,
  [SESSION_STATUS.TBA]: ({ session }) => <TBAScreen sessionTitle={session?.data?.detail?.title} />,
  [SESSION_STATUS.ENDED]: ({ session }) => <SessionEndedScreen sessionTitle={session?.data?.detail?.title} />,
  [SESSION_STATUS.ON_HOLD]: ({ session }) => <OnHoldScreen sessionTitle={session?.data?.detail?.title} />,
  [SESSION_STATUS.CANCELED]: ({ session }) => <CanceledScreen sessionTitle={session?.data?.detail?.title} />,
  [SESSION_STATUS.RESCHEDULED]: ({ session }) => <RescheduledScreen sessionTitle={session?.data?.detail?.title} />,
};

function LoadingView() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
        <p className="text-gray-500 text-sm">Loading session...</p>
      </div>
    </div>
  );
}

function UserDetailsErrorView({ title, message, onRetry }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
      <div className="text-center max-w-sm">
        <p className="font-semibold text-gray-800 mb-2">{title}</p>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        {onRetry && (
          <button onClick={onRetry} className="px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors">
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

// children: a function called only when the session is live, so it can safely read session.data.
// userDetailsFailed / userNotVerified: Ably never connects without the moderator's user
// details (see useSessionState), so these win over the loading view.
export function SessionGate({ loading, session, userDetailsFailed, userNotVerified, children }) {
  if (userDetailsFailed) {
    return (
      <UserDetailsErrorView
        title="We're facing some issues"
        message="We couldn't load your details, so the session can't start right now. Please try again in a few moments."
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (userNotVerified) {
    return (
      <UserDetailsErrorView
        title="Sorry, we couldn't verify you"
        message="We couldn't confirm your account for this session. Please contact your session administrator."
      />
    );
  }

  if (loading) {
    return <LoadingView />;
  }

  if (!session) {
    // This screen is also reached by useSessionState's loading timeout, not
    // just a genuinely missing session - a slow/flaky Ably connection can
    // land here too. Retry (a full reload) re-runs the whole connect +
    // session-load pipeline from scratch, which a stale in-app retry can't
    // cleanly do since it'd need to re-init Ably and every sync hook.
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl font-bold text-gray-300 mb-4">404</div>
          <p className="text-gray-500 mb-6">Session not found</p>
          <button onClick={() => window.location.reload()} className="px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const status = session.data?.detail?.status;
  const isLive = String(status) === String(SESSION_STATUS.LIVE);
  if (!isLive) {
    if (status == null) return <LoadingView />;
    const StatusPage = STATUS_PAGES[status] || UnknownScreen;
    return <StatusPage session={session} />;
  }

  return children();
}

export default SessionGate;
